import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { useReactiveLang } from "../lib/i18n";

const CONTACT = "legal@islandsdaily.com";
const LAST_UPDATED = "February 2026";

const LANGS = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
  { code: "es", label: "Español" },
];

const COPY = {
  en: {
    title: "Terms & Copyright",
    last_updated: `Last updated: ${LAST_UPDATED}`,
    sections: [
      {
        h: "About IslandsDaily",
        p: "IslandsDaily is a news aggregation and translation service covering the Dutch Caribbean islands of Aruba, Curaçao, Bonaire, and St. Maarten. Our platform collects publicly available headline-level information from leading local news sources and presents AI-generated English summaries to make Caribbean news accessible to tourists, expats, and English-speaking residents across the region.",
      },
      {
        h: "What We Display",
        p: "IslandsDaily displays only the following information per article: the original article headline, an AI-generated summary of between 5 and 7 sentences derived from the publicly available article excerpt, up to three key facts, and the article's thumbnail image served directly from the original publisher's server. We do not display, store, or distribute full article body text under any circumstances. Every article on IslandsDaily includes a clearly visible link directing readers to the full original article on the publisher's own website, where it can be read in the original language.",
      },
      {
        h: "Copyright & Intellectual Property",
        p: "All original articles, full content, and images displayed on IslandsDaily remain the exclusive intellectual property of their respective original publishers. IslandsDaily makes no claim of ownership over any content originating from third-party news sources. Our AI-generated English summaries constitute derivative works created for the purpose of language accessibility and do not reproduce original article text. Article thumbnail images are served directly from original publisher servers via standard hotlinking and are not downloaded, copied, stored, or re-hosted on IslandsDaily infrastructure.",
      },
      {
        h: "Our Legal Framework",
        p: "IslandsDaily operates in the internationally established tradition of news aggregation as practiced by Google News, Apple News, Flipboard, and thousands of platforms worldwide. We operate in good faith, maintain full attribution to all original sources, actively direct reader traffic back to original publishers, and limit displayed content strictly to headline-level information. We do not reproduce copyrighted content and we do not derive commercial benefit from the reproduction of original articles.",
      },
      {
        h: "Content Removal Requests",
        p: "If you are a content owner, publisher, or rights holder and you wish to request the removal or modification of how your content appears on IslandsDaily, please contact us at the email address below. We take all such requests seriously and will respond within 48 hours. We will remove any content upon valid request without requiring legal proceedings. We believe in resolving all content concerns directly and amicably.",
        contact: true,
      },
      {
        h: "Advertiser Disclaimer",
        p: "Sponsored content and advertisements displayed on IslandsDaily are clearly labeled as 'Sponsored' or 'Advertisement' and are separate from editorial news content. IslandsDaily does not endorse any advertiser's products or services. Advertisers have no influence over editorial content or the selection of news articles displayed on the platform.",
      },
      {
        h: "WhatsApp Digest Service",
        p: "By subscribing to the IslandsDaily WhatsApp digest, you agree to receive up to one (1) automated WhatsApp message per day containing curated news headlines for your selected island(s) in your chosen language. Message and data rates may apply depending on your mobile carrier or plan. You may unsubscribe at any time by replying STOP to any digest message. Reply HELP for assistance. For privacy questions related to the digest service, see our Privacy Policy.",
        p_after:
          "No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. All other categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.",
      },
      {
        h: "External Links",
        p: "IslandsDaily contains links to external websites including original news source articles. We are not responsible for the content, accuracy, or privacy practices of any external website.",
      },
      {
        h: "Changes to These Terms",
        p: "IslandsDaily reserves the right to update these terms at any time. Continued use of the platform following any changes constitutes acceptance of the updated terms. The date of the most recent update is displayed at the top of this page.",
      },
      {
        h: "Contact",
        p: "For all inquiries including content removal requests, advertising, partnerships, and general questions:",
        contact_block: true,
      },
    ],
    contact_owners: "Content owners & press",
    email_label: "Email",
    website_label: "Website",
  },
  nl: {
    title: "Voorwaarden & Auteursrecht",
    last_updated: `Laatst bijgewerkt: ${LAST_UPDATED}`,
    sections: [
      {
        h: "Over IslandsDaily",
        p: "IslandsDaily is een nieuwsaggregatiedienst die het Nederlandse Caribisch gebied bestrijkt, waaronder Aruba, Curaçao, Bonaire en Sint Maarten. Ons platform verzamelt openbaar beschikbare kopregelinformatie van toonaangevende lokale nieuwsbronnen en presenteert AI-gegenereerde Engelse samenvattingen om Caribisch nieuws toegankelijk te maken voor toeristen, expats en Engelstalige inwoners in de regio.",
      },
      {
        h: "Wat Wij Weergeven",
        p: "IslandsDaily geeft per artikel alleen de volgende informatie weer: de originele kop van het artikel, een AI-gegenereerde samenvatting van vijf tot zeven zinnen afgeleid van het openbaar beschikbare artikelfragment, maximaal drie kernfeiten en de miniatuurafbeelding van het artikel, rechtstreeks geserveerd vanaf de server van de oorspronkelijke uitgever. Wij slaan onder geen enkele omstandigheid de volledige artikeltekst op, geven deze niet weer en verspreiden deze niet. Elk artikel op IslandsDaily bevat een duidelijk zichtbare link die lezers doorverwijst naar het volledige originele artikel op de eigen website van de uitgever, waar het in de oorspronkelijke taal kan worden gelezen.",
      },
      {
        h: "Auteursrecht & Intellectueel Eigendom",
        p: "Alle originele artikelen, volledige inhoud en afbeeldingen op IslandsDaily blijven het exclusieve intellectuele eigendom van hun respectieve oorspronkelijke uitgevers. IslandsDaily maakt geen aanspraak op eigendom van inhoud afkomstig van nieuwsbronnen van derden. Onze AI-gegenereerde Engelse samenvattingen vormen afgeleide werken die zijn gemaakt ten behoeve van taaltoegang en reproduceren geen originele artikeltekst. Miniatuurafbeeldingen worden rechtstreeks geserveerd vanaf de servers van de oorspronkelijke uitgevers via standaard hotlinking en worden niet gedownload, gekopieerd, opgeslagen of opnieuw gehost op IslandsDaily-infrastructuur.",
      },
      {
        h: "Ons Juridisch Kader",
        p: "IslandsDaily opereert binnen de internationaal gevestigde traditie van nieuwsaggregatie zoals beoefend door Google News, Apple News, Flipboard en duizenden platforms wereldwijd. Wij opereren te goeder trouw, handhaven volledige toeschrijving aan alle originele bronnen, sturen lezersverkeer actief terug naar originele uitgevers en beperken weergegeven inhoud strikt tot informatie op kopregelniveau.",
      },
      {
        h: "Verzoeken tot Verwijdering van Inhoud",
        p: "Als u een inhoudseigenaar, uitgever of rechthebbende bent en u wenst te verzoeken om verwijdering of aanpassing van de manier waarop uw inhoud op IslandsDaily verschijnt, neem dan contact met ons op via het onderstaande e-mailadres. Wij nemen alle dergelijke verzoeken serieus en zullen binnen 48 uur reageren.",
        contact: true,
      },
      {
        h: "WhatsApp Digest-dienst",
        p: "Door u te abonneren op de IslandsDaily WhatsApp-digest gaat u ermee akkoord maximaal één (1) geautomatiseerd WhatsApp-bericht per dag te ontvangen met samengestelde nieuwskoppen voor uw geselecteerde eiland(en) in uw gekozen taal. Er kunnen bericht- en datakosten van toepassing zijn, afhankelijk van uw mobiele provider of abonnement. U kunt zich op elk moment afmelden door STOP te antwoorden op een digest-bericht. Antwoord HELP voor hulp. Voor privacyvragen met betrekking tot de digest-dienst, zie ons Privacybeleid.",
        p_after:
          "Geen mobiele informatie wordt gedeeld met derden of gelieerde ondernemingen voor marketing- of promotionele doeleinden. Alle andere categorieën sluiten opt-in-gegevens en toestemming van de afzender van sms-berichten uit; deze informatie zal niet worden gedeeld met derden.",
      },
      {
        h: "Wijzigingen in Deze Voorwaarden",
        p: "IslandsDaily behoudt zich het recht voor deze voorwaarden op elk moment bij te werken. Voortgezet gebruik van het platform na eventuele wijzigingen betekent aanvaarding van de bijgewerkte voorwaarden.",
      },
      {
        h: "Contact",
        contact_block: true,
      },
    ],
    contact_owners: "Inhoudseigenaren & pers",
    email_label: "E-mail",
    website_label: "Website",
  },
  es: {
    title: "Términos y Derechos de Autor",
    last_updated: `Última actualización: ${LAST_UPDATED}`,
    sections: [
      {
        h: "Acerca de IslandsDaily",
        p: "IslandsDaily es un servicio de agregación y traducción de noticias que cubre las islas del Caribe neerlandés: Aruba, Curaçao, Bonaire y Sint Maarten. Nuestra plataforma recopila información de titulares disponible públicamente de las principales fuentes de noticias locales y presenta resúmenes en inglés generados por inteligencia artificial para hacer las noticias del Caribe accesibles a turistas, expatriados y residentes de habla inglesa en la región.",
      },
      {
        h: "Qué Mostramos",
        p: "IslandsDaily muestra únicamente la siguiente información por artículo: el titular original del artículo, un resumen generado por IA de entre cinco y siete oraciones derivado del extracto del artículo disponible públicamente, hasta tres datos clave y la imagen en miniatura del artículo servida directamente desde el servidor del editor original. No mostramos, almacenamos ni distribuimos el texto completo del cuerpo del artículo bajo ninguna circunstancia. Cada artículo en IslandsDaily incluye un enlace claramente visible que dirige a los lectores al artículo original completo en el sitio web del editor, donde puede ser leído en el idioma original.",
      },
      {
        h: "Derechos de Autor y Propiedad Intelectual",
        p: "Todos los artículos originales, el contenido completo y las imágenes mostradas en IslandsDaily siguen siendo propiedad intelectual exclusiva de sus respectivos editores originales. IslandsDaily no reclama propiedad sobre ningún contenido originado en fuentes de noticias de terceros. Nuestros resúmenes en inglés generados por IA constituyen obras derivadas creadas con el propósito de accesibilidad lingüística y no reproducen el texto original del artículo. Las imágenes en miniatura se sirven directamente desde los servidores de los editores originales mediante hotlinking estándar y no se descargan, copian, almacenan ni alojan en la infraestructura de IslandsDaily.",
      },
      {
        h: "Nuestro Marco Legal",
        p: "IslandsDaily opera en la tradición internacionalmente establecida de agregación de noticias practicada por Google News, Apple News, Flipboard y miles de plataformas en todo el mundo. Operamos de buena fe, mantenemos plena atribución a todas las fuentes originales y dirigimos activamente el tráfico de lectores de vuelta a los editores originales.",
      },
      {
        h: "Solicitudes de Eliminación de Contenido",
        p: "Si usted es propietario de contenido, editor o titular de derechos y desea solicitar la eliminación o modificación de cómo aparece su contenido en IslandsDaily, contáctenos en la dirección de correo electrónico que se indica a continuación. Tomamos todas estas solicitudes en serio y responderemos dentro de las 48 horas.",
        contact: true,
      },
      {
        h: "Servicio de Resumen por WhatsApp",
        p: "Al suscribirse al resumen diario por WhatsApp de IslandsDaily, usted acepta recibir hasta un (1) mensaje automatizado de WhatsApp por día con titulares de noticias seleccionados para su(s) isla(s) elegida(s) en el idioma que haya elegido. Pueden aplicarse cargos por mensajes y datos según su operador o plan móvil. Puede darse de baja en cualquier momento respondiendo STOP a cualquier mensaje del resumen. Responda HELP para obtener ayuda. Para preguntas sobre privacidad relacionadas con el servicio de resumen, consulte nuestra Política de Privacidad.",
        p_after:
          "Ninguna información móvil será compartida con terceros o afiliados para fines de marketing o promocionales. Todas las demás categorías excluyen los datos de consentimiento y aceptación (opt-in) del originador de mensajes de texto; esta información no será compartida con ningún tercero.",
      },
      {
        h: "Cambios en Estos Términos",
        p: "IslandsDaily se reserva el derecho de actualizar estos términos en cualquier momento. El uso continuado de la plataforma tras cualquier cambio constituye la aceptación de los términos actualizados.",
      },
      {
        h: "Contacto",
        contact_block: true,
      },
    ],
    contact_owners: "Propietarios de contenido y prensa",
    email_label: "Correo electrónico",
    website_label: "Sitio web",
  },
};

export default function TermsPage() {
  // __TOGGLE_HOOK__: self-contained reactive lang state for the mini-header toggle.
  const [__toggleLang, __setToggleLang] = useReactiveLang();

  const [lang, setLang] = useState("en");
  const copy = COPY[lang];

  useEffect(() => {
    document.title = "Terms & Copyright — IslandsDaily";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="terms-back-btn"
          >
            <ArrowLeft size={15} /> Back
          </Link>
          <Link
            to="/"
            className="ml-auto font-serif-display text-[18px] leading-none"
          >
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          {/* __LANG_TOGGLE__ */}
          <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
        </div>
      </div>

      <article
        className="mx-auto px-4 sm:px-6 py-10 sm:py-14"
        style={{ maxWidth: "760px" }}
      >
        {/* Title */}
        <h1
          className="font-serif-display text-[32px] sm:text-[36px] leading-tight text-[#1A2E35] text-center"
          data-testid="terms-heading"
        >
          {copy.title}
        </h1>
        <p className="text-center text-[12px] text-[#6B7C82] mt-2 mb-8">
          {copy.last_updated}
        </p>

        {/* Language tabs */}
        <div
          className="flex items-center justify-center gap-2 mb-10"
          role="tablist"
          data-testid="terms-lang-tabs"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="tab"
              aria-selected={lang === l.code}
              onClick={() => setLang(l.code)}
              className={`px-4 py-1.5 rounded-full text-[12px] transition-colors ${
                lang === l.code
                  ? "bg-[#0AADA0] text-white"
                  : "bg-[#F5F5F5] text-[#1A2E35] hover:bg-[#EEEEEE]"
              }`}
              data-testid={`terms-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Sections */}
        <div
          className="space-y-7 text-[16px] text-[#1A2E35]"
          style={{
            lineHeight: 1.8,
            fontFamily: "'DM Sans', system-ui, sans-serif",
          }}
          data-testid={`terms-content-${lang}`}
        >
          {copy.sections.map((s, i) => (
            <section key={i} data-testid={`terms-section-${i}`}>
              {i > 0 && <div className="h-px bg-[#EEEEEE] mb-7" />}
              <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-3">
                {s.h}
              </h2>
              {s.p && <p>{s.p}</p>}
              {s.p_after && <p className="mt-3">{s.p_after}</p>}
              {s.contact && (
                <p className="mt-3">
                  <span className="text-[#1A2E35]">{copy.contact_owners}: </span>
                  <a
                    href={`mailto:${CONTACT}`}
                    className="text-[#0AADA0] hover:underline"
                    data-testid={`terms-contact-link-${i}`}
                  >
                    {CONTACT}
                  </a>
                </p>
              )}
              {s.contact_block && (
                <div className="mt-3 space-y-1">
                  <div>
                    {copy.email_label}:{" "}
                    <a
                      href={`mailto:${CONTACT}`}
                      className="text-[#0AADA0] hover:underline"
                      data-testid="terms-contact-link"
                    >
                      {CONTACT}
                    </a>
                  </div>
                  <div>
                    {copy.website_label}:{" "}
                    <a
                      href="https://islandsdaily.com"
                      className="text-[#0AADA0] hover:underline"
                    >
                      islandsdaily.com
                    </a>
                  </div>
                </div>
              )}
            </section>
          ))}
        </div>

        <div className="h-px bg-[#EEEEEE] my-10" />
        <p className="font-serif-display italic text-[#0AADA0] text-[20px] text-center">
          Aruba, Curaçao, Bonaire &amp; St. Maarten — caught up.
        </p>
      </article>
      <Footer />
    </div>
  );
}
